import { zodResolver } from '@hookform/resolvers/zod';
import InfoIcon from '@mui/icons-material/Info';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { BuildingRequest } from '../../types';
import {
  BAY_SPACING_OPTIONS,
  POST_SIZE_OPTIONS,
  postSizeDisplayToEnum,
  WidthMeasurementReference,
} from './building-constants';
import { useGetOrderByIdQuery } from './orders-api-slice';

// Validation schema for building
const buildingSchema = z.object({
  name: z
    .string()
    .min(1, 'Building name is required')
    .max(250, 'Building name must be 250 characters or less'),
  width: z
    .union([
      z
        .number()
        .int('Enter a valid whole number for building width')
        .min(0, 'Building width must be 0 or greater')
        .max(999, 'Building width must be 999 or less'),
      z.undefined(),
    ])
    .optional(),
  length: z
    .union([
      z
        .number()
        .int('Enter a valid whole number for building length')
        .min(0, 'Building length must be 0 or greater')
        .max(999, 'Building length must be 999 or less'),
      z.undefined(),
    ])
    .optional(),
  height: z
    .union([
      z
        .number()
        .int('Enter a valid whole number for building height')
        .min(0, 'Building height must be 0 or greater')
        .max(99, 'Building height must be 99 or less'),
      z.undefined(),
    ])
    .optional(),
  baySpacing: z.number().int('Bay spacing is required').min(1, 'Bay spacing must be at least 1'),
  postSize: z.string().min(1, 'Post size is required'),
  application: z.enum(['Residential', 'Agricultural'], {
    errorMap: () => ({ message: 'Application is required' }),
  }),
  additionalNotes: z
    .string()
    .max(2000, 'Additional notes must be 2000 characters or less')
    .optional(),
  isPermanentDwelling: z.boolean(),
  includePermanentDwellingWithPoleBarn: z.boolean().nullable().optional(),
  includePermanentDwellingForPartiallyClosedPoleBarn: z.boolean().nullable().optional(),
  includePermanentDwellingForFullyEnclosedPoleBarn: z.boolean().nullable().optional(),
  includePermanentDwellingWithNoMEP: z.boolean().nullable().optional(),
});

type BuildingFormData = z.infer<typeof buildingSchema>;

interface AddBuildingDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (building: BuildingRequest) => Promise<void>;
  orderId: number;
  existingBuildingNames?: string[];
  isSubmitting?: boolean;
}

const AddBuildingDialog: React.FC<AddBuildingDialogProps> = ({
  open,
  onClose,
  onSubmit,
  orderId,
  existingBuildingNames = [],
  isSubmitting = false,
}) => {
  const [uniqueNameError, setUniqueNameError] = useState<string>('');

  // Get order to get engineering fees
  const { data: order } = useGetOrderByIdQuery(orderId.toString(), { skip: !orderId });
  // Engineering fees are now included in the order response
  const engineeringFees = order?.engineeringFees;

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isValid },
  } = useForm<BuildingFormData>({
    resolver: zodResolver(buildingSchema),
    defaultValues: {
      name: '',
      width: undefined,
      length: undefined,
      height: undefined,
      baySpacing: 10,
      postSize: '6x6',
      application: 'Residential',
      additionalNotes: '',
      isPermanentDwelling: false,
      includePermanentDwellingWithPoleBarn: false,
      includePermanentDwellingForPartiallyClosedPoleBarn: false,
      includePermanentDwellingForFullyEnclosedPoleBarn: false,
      includePermanentDwellingWithNoMEP: false,
    },
    mode: 'onTouched',
  });

  const isPermanentDwelling = watch('isPermanentDwelling');
  const buildingName = watch('name');

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      reset();
      setUniqueNameError('');
    }
  }, [open, reset]);

  // Validate unique building name
  useEffect(() => {
    if (buildingName && existingBuildingNames.includes(buildingName.trim())) {
      setUniqueNameError('Building name must be unique within this order');
    } else {
      setUniqueNameError('');
    }
  }, [buildingName, existingBuildingNames]);

  const handleClose = () => {
    reset();
    setUniqueNameError('');
    onClose();
  };

  const handleDialogClose = (_event: object, reason: string) => {
    // Prevent closing when clicking outside the dialog (backdrop click)
    if (reason === 'backdropClick') {
      return;
    }
    handleClose();
  };

  const onSubmitHandler = handleSubmit(async data => {
    if (uniqueNameError) {
      return;
    }

    const buildingRequest: BuildingRequest = {
      orderId,
      name: data.name.trim(),
      confirmedLoadRating: false,
      width: data.width,
      length: data.length,
      height: data.height,
      baySpacing: data.baySpacing,
      // Convert display name (e.g., "6x6") to enum name (e.g., "SixBySix")
      postSize: postSizeDisplayToEnum[data.postSize] || data.postSize,
      widthMeasurementReference: WidthMeasurementReference.OutsideOutside, // Default for new buildings
      application: data.application,
      additionalNotes: data.additionalNotes?.trim() || undefined,
      isPermanentDwelling: data.isPermanentDwelling,
      includePermanentDwellingWithPoleBarn: data.isPermanentDwelling
        ? data.includePermanentDwellingWithPoleBarn || false
        : undefined,
      includePermanentDwellingForPartiallyClosedPoleBarn: data.isPermanentDwelling
        ? data.includePermanentDwellingForPartiallyClosedPoleBarn || false
        : undefined,
      includePermanentDwellingForFullyEnclosedPoleBarn: data.isPermanentDwelling
        ? data.includePermanentDwellingForFullyEnclosedPoleBarn || false
        : undefined,
      includePermanentDwellingWithNoMEP: data.isPermanentDwelling
        ? data.includePermanentDwellingWithNoMEP || false
        : undefined,
    };

    await onSubmit(buildingRequest);
    handleClose();
  });

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle>Add Building</DialogTitle>
      <DialogContent>
        <form onSubmit={onSubmitHandler} id="add-building-form">
          <Box sx={{ pt: 2 }}>
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
              Please enter the Building information
            </Typography>

            {/* Building Name */}
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Building Name"
                  required
                  error={!!errors.name || !!uniqueNameError}
                  helperText={
                    errors.name?.message ||
                    uniqueNameError ||
                    'Enter a unique name for this building'
                  }
                  sx={{ mb: 3 }}
                />
              )}
            />

            {/* Three-column layout: Building Dimensions (left), Other (middle), Engineering Plan Fees (right) */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {/* Left Column - Building Dimensions */}
              <Grid item xs={12} md={4}>
                <Paper
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    height: '100%',
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Building Dimensions
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Controller
                      name="width"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Building Width (ft.)"
                          type="number"
                          inputProps={{ min: 0, max: 999, step: 1 }}
                          value={field.value ?? ''}
                          onChange={e => {
                            const value =
                              e.target.value === '' ? undefined : parseInt(e.target.value, 10);
                            field.onChange(value);
                          }}
                          error={!!errors.width}
                          helperText={errors.width?.message}
                        />
                      )}
                    />
                    <Controller
                      name="length"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Building Length (ft.)"
                          type="number"
                          inputProps={{ min: 0, max: 999, step: 1 }}
                          value={field.value ?? ''}
                          onChange={e => {
                            const value =
                              e.target.value === '' ? undefined : parseInt(e.target.value, 10);
                            field.onChange(value);
                          }}
                          error={!!errors.length}
                          helperText={errors.length?.message}
                        />
                      )}
                    />
                    <Controller
                      name="height"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Building Height (ft.)"
                          type="number"
                          inputProps={{ min: 0, max: 99, step: 1 }}
                          value={field.value ?? ''}
                          onChange={e => {
                            const value =
                              e.target.value === '' ? undefined : parseInt(e.target.value, 10);
                            field.onChange(value);
                          }}
                          error={!!errors.height}
                          helperText={errors.height?.message}
                        />
                      )}
                    />
                    <Controller
                      name="baySpacing"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth required error={!!errors.baySpacing}>
                          <FormLabel>Bay Spacing (ft.)</FormLabel>
                          <Select {...field}>
                            {BAY_SPACING_OPTIONS.map(value => (
                              <MenuItem key={value} value={value}>
                                {value}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.baySpacing && (
                            <FormHelperText>{errors.baySpacing.message}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                    <Controller
                      name="postSize"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth required error={!!errors.postSize}>
                          <FormLabel>Post Size (ft.)</FormLabel>
                          <Select {...field}>
                            {POST_SIZE_OPTIONS.map(value => (
                              <MenuItem key={value} value={value}>
                                {value}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.postSize && (
                            <FormHelperText>{errors.postSize.message}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Box>
                </Paper>
              </Grid>

              {/* Middle Column - Other */}
              <Grid item xs={12} md={4}>
                <Paper
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    height: '100%',
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Other
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Application */}
                    <FormControl component="fieldset" required error={!!errors.application}>
                      <FormLabel component="legend">Application</FormLabel>
                      <Controller
                        name="application"
                        control={control}
                        render={({ field }) => (
                          <RadioGroup {...field} row>
                            <FormControlLabel
                              value="Residential"
                              control={<Radio />}
                              label="Residential"
                            />
                            <FormControlLabel
                              value="Agricultural"
                              control={<Radio />}
                              label="Agricultural"
                            />
                          </RadioGroup>
                        )}
                      />
                      {errors.application && (
                        <FormHelperText>{errors.application.message}</FormHelperText>
                      )}
                    </FormControl>

                    {/* Additional Notes */}
                    <Controller
                      name="additionalNotes"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Additional Notes"
                          multiline
                          rows={4}
                          inputProps={{ maxLength: 2000 }}
                          helperText={`${field.value?.length || 0}/2000 characters`}
                        />
                      )}
                    />

                    {/* Permanent Dwelling */}
                    <Controller
                      name="isPermanentDwelling"
                      control={control}
                      render={({ field }) => (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                          <FormControlLabel
                            control={<Checkbox {...field} checked={field.value} />}
                            label="Permanent Dwelling"
                          />
                          <Tooltip
                            title={
                              <Box sx={{ p: 0.5 }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                  A permanent dwelling is generally defined as a fixed residence
                                  intended for continuous or long-term occupancy. It typically
                                  refers to a building or structure, including homes, houses, mobile
                                  homes, or units in multi-unit residential facilities, that are
                                  designed for living and equipped with necessary amenities like
                                  plumbing, electrical, heating, and air conditioning systems.
                                  Essentially, it's a place where someone lives and stays, not a
                                  temporary or transient accommodation.
                                </Typography>
                                <Typography variant="body2">
                                  Truss US strongly suggests engineered building plans for all
                                  permanent dwellings and they are required by local municipalities.
                                  While these plans do not have to be purchased through Truss US, we
                                  do sell them.{' '}
                                  <Typography
                                    component="span"
                                    variant="body2"
                                    sx={{ fontWeight: 'bold' }}
                                  >
                                    See Engineered Plan Fees
                                  </Typography>{' '}
                                  section for more details.
                                </Typography>
                              </Box>
                            }
                            arrow
                            placement="right"
                            sx={{ maxWidth: 400 }}
                          >
                            <IconButton size="small" sx={{ p: 0.25, ml: -0.75 }}>
                              <InfoIcon fontSize="small" color="action" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    />
                  </Box>
                </Paper>
              </Grid>

              {/* Right Column - Engineered Plan Fees */}
              <Grid item xs={12} md={4}>
                <Paper
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    height: '100%',
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Engineered Plan Fees
                  </Typography>
                  {engineeringFees ? (
                    <>
                      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        Please select which fees to include
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Controller
                          name="includePermanentDwellingWithPoleBarn"
                          control={control}
                          render={({ field }) => (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  {...field}
                                  checked={field.value || false}
                                  disabled={!isPermanentDwelling}
                                />
                              }
                              label={`Open Pole Barn - $${engineeringFees.openPoleBarnFee.toFixed(
                                2
                              )}`}
                            />
                          )}
                        />
                        <Controller
                          name="includePermanentDwellingForPartiallyClosedPoleBarn"
                          control={control}
                          render={({ field }) => (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  {...field}
                                  checked={field.value || false}
                                  disabled={!isPermanentDwelling}
                                />
                              }
                              label={`Partial Closed Pole Barn - $${engineeringFees.partialClosedPoleBarnFee.toFixed(
                                2
                              )}`}
                            />
                          )}
                        />
                        <Controller
                          name="includePermanentDwellingForFullyEnclosedPoleBarn"
                          control={control}
                          render={({ field }) => (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  {...field}
                                  checked={field.value || false}
                                  disabled={!isPermanentDwelling}
                                />
                              }
                              label={`Fully Enclosed Pole Barn - $${engineeringFees.fullyEnclosedPoleBarnFee.toFixed(
                                2
                              )}`}
                            />
                          )}
                        />
                        <Controller
                          name="includePermanentDwellingWithNoMEP"
                          control={control}
                          render={({ field }) => (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  {...field}
                                  checked={field.value || false}
                                  disabled={!isPermanentDwelling}
                                />
                              }
                              label={`Permanent Dwelling (no MEP) - $${engineeringFees.permanentDwellingFee.toFixed(
                                2
                              )}`}
                            />
                          )}
                        />
                      </Box>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Engineering fees not available
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="add-building-form"
          variant="contained"
          color="success"
          disabled={!isValid || !!uniqueNameError || isSubmitting}
        >
          {isSubmitting ? 'Adding...' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddBuildingDialog;
